import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button } from '@heroui/react';

import { useApi } from '../lib/api-context';
import {
  notifySessionsChanged,
  useSessionList,
} from '../hooks/useSessionList';
import { useSessions } from '../hooks/useSessions';
import { usePersistentToggle } from '../hooks/usePersistentToggle';
import type { SessionMeta } from '../lib/api-client';
import { AddMacModal } from './AddMacModal';
import { SidebarHandle } from './SidebarHandle';

const STORAGE_KEY = 'bluebird.sidebar.left.v1';
const MIN_WIDTH = 220;
const MAX_WIDTH = 480;
const DEFAULT_WIDTH = 280;

interface LeftSidebarProps {
  /** Currently active tab id (chat or file). Highlights the matching session row. */
  activeTabId: string | null;
}

/**
 * Collapsible left sidebar — canonical session list (server-backed).
 *
 * Clicking a row that's already open as a tab focuses that tab; clicking
 * a closed session opens a new chat tab with the matching id. The trash
 * affordance opens a confirm modal that hits `DELETE /api/sessions/{id}`
 * and closes any open tab in one shot.
 */
export function LeftSidebar({ activeTabId }: LeftSidebarProps) {
  const { open, width, toggle, setWidth } = usePersistentToggle(STORAGE_KEY, {
    width: DEFAULT_WIDTH,
  });
  const { sessions, loading, error, refresh } = useSessionList();
  const { tabs, activeId, createTab, removeTab, focusTab } = useSessions();
  const api = useApi();
  const [filter, setFilter] = useState('');
  const [pendingDelete, setPendingDelete] = useState<SessionMeta | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [addMacOpen, setAddMacOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const sorted = [...sessions].sort((a, b) =>
      b.updatedAt.localeCompare(a.updatedAt),
    );
    if (!q) return sorted;
    return sorted.filter((s) => s.name.toLowerCase().includes(q));
  }, [sessions, filter]);

  const openSessionIds = useMemo(() => {
    const set = new Set<string>();
    for (const t of tabs) {
      if (t.kind === 'chat') set.add(t.id);
    }
    return set;
  }, [tabs]);

  const handleSelect = useCallback(
    (sessionId: string, name: string) => {
      // If a tab is already open for this session, just focus it.
      if (openSessionIds.has(sessionId)) {
        focusTab(sessionId);
        return;
      }
      // Otherwise create the tab using the server's id + title (so the
      // history hydrates from the same DO session) and focus it.
      createTab({ id: sessionId, title: name });
      focusTab(sessionId);
    },
    [createTab, focusTab, openSessionIds],
  );

  const handleNewChat = useCallback(() => {
    const tab = createTab();
    focusTab(tab.id);
  }, [createTab, focusTab]);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteSession(pendingDelete.id);
      // Close the chat tab AND every dependent workspace file tab
      // (`scope === 'workspace' && sessionId === pendingDelete.id`).
      // The sandbox container backing those file tabs is destroyed
      // server-side by `Bluebird.deleteSession`, so any open viewer
      // would 404 on its next refresh anyway. Collect the ids
      // before mutating so we can also exclude them when picking the
      // fallback active tab below.
      const dependentIds = new Set<string>([pendingDelete.id]);
      for (const t of tabs) {
        if (
          t.kind === 'file' &&
          t.scope === 'workspace' &&
          t.sessionId === pendingDelete.id
        ) {
          dependentIds.add(t.id);
        }
      }
      for (const id of dependentIds) removeTab(id);
      // If the deleted session (or one of its workspace file tabs)
      // was active, focus a sibling tab that's NOT in the dependent
      // set. If none remain, mint a fresh "New chat" so the
      // workspace always has an active pane — leaving the URL
      // pointing at a closed tab would render an empty `<main>`
      // until the user took manual action.
      if (activeId && dependentIds.has(activeId)) {
        const next = tabs.find((t) => !dependentIds.has(t.id));
        if (next) {
          focusTab(next.id);
        } else {
          const fresh = createTab();
          focusTab(fresh.id);
        }
      }
      setPendingDelete(null);
      notifySessionsChanged();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  }, [
    api,
    pendingDelete,
    removeTab,
    activeId,
    focusTab,
    tabs,
    createTab,
  ]);

  useEffect(() => {
    // Clear stale error when modal closes.
    if (!pendingDelete) setDeleteError(null);
  }, [pendingDelete]);

  if (!open) {
    return (
      <aside
        aria-label="Sessions sidebar (collapsed)"
        className="flex w-10 flex-col border-e border-white/8 py-2"
      >
        <Button
          isIconOnly
          variant="ghost"
          size="sm"
          aria-label="Expand sessions sidebar"
          onPress={toggle}
        >
          <span aria-hidden="true">▶</span>
        </Button>
      </aside>
    );
  }

  return (
    <>
      <aside
        aria-label="Sessions"
        className="flex h-full min-h-0 flex-col border-e border-white/8 bg-white/2"
        style={{ width: `${width}px`, flex: `0 0 ${width}px` }}
      >
        <header className="flex items-center gap-2 border-b border-white/8 px-3 py-2 text-xs">
          <span className="font-semibold uppercase tracking-wider opacity-70">
            Sessions
          </span>
          <span className="ms-auto flex items-center gap-1">
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              aria-label="New chat"
              onPress={handleNewChat}
            >
              <span aria-hidden="true">＋</span>
            </Button>
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              aria-label="Refresh sessions"
              onPress={refresh}
            >
              <span aria-hidden="true">↻</span>
            </Button>
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              aria-label="Collapse sidebar"
              onPress={toggle}
            >
              <span aria-hidden="true">◀</span>
            </Button>
          </span>
        </header>

        <div className="border-b border-white/8 px-3 py-2">
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter…"
            aria-label="Filter sessions"
            className="w-full rounded-md bg-white/5 px-2 py-1.5 text-sm outline-none ring-1 ring-white/8 focus:ring-blue-400/60"
          />
        </div>

        <div className="scroll-pane min-h-0 flex-1 overflow-y-auto py-1">
          {loading && sessions.length === 0 && (
            <div className="px-3 py-4 text-xs opacity-60">Loading…</div>
          )}
          {error && (
            <div className="px-3 py-4 text-xs text-rose-300">
              {error}
            </div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="px-3 py-4 text-xs opacity-60">
              {sessions.length === 0
                ? 'No sessions yet — start a new chat.'
                : 'No matches.'}
            </div>
          )}
          <ul role="list" className="flex flex-col">
            {filtered.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                isActive={activeTabId === s.id}
                isOpen={openSessionIds.has(s.id)}
                onSelect={() => handleSelect(s.id, s.name)}
                onDelete={() => setPendingDelete(s)}
              />
            ))}
          </ul>
        </div>

        {/* Footer — devices entry point. Distinct from the sessions list
            so it stays visible even when the list scrolls. */}
        <footer className="border-t border-white/8 px-3 py-2">
          <button
            type="button"
            onClick={() => setAddMacOpen(true)}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-zinc-300 transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60"
          >
            <span aria-hidden="true">＋</span>
            <span>Add a Mac</span>
            <span className="ms-auto text-[10px] opacity-60">device</span>
          </button>
        </footer>
      </aside>
      <SidebarHandle
        side="left"
        width={width}
        setWidth={setWidth}
        min={MIN_WIDTH}
        max={MAX_WIDTH}
        ariaLabel="Resize sessions sidebar"
      />

      {pendingDelete !== null && (
        <ConfirmDeleteDialog
          name={pendingDelete.name}
          deleting={deleting}
          error={deleteError}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => void handleConfirmDelete()}
        />
      )}

      <AddMacModal
        isOpen={addMacOpen}
        onClose={() => setAddMacOpen(false)}
      />
    </>
  );
}

interface ConfirmDeleteDialogProps {
  name: string;
  deleting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Minimal confirmation dialog. Tailwind-styled rather than HeroUI's
 * Modal because HeroUI v3 Modal is built on react-aria-components'
 * `DialogTrigger` pattern (declarative trigger + open subtree), which
 * is awkward to drive imperatively from `isOpen`. Auto-focuses the
 * cancel button so Esc/Enter behave as expected.
 */
function ConfirmDeleteDialog({
  name,
  deleting,
  error,
  onCancel,
  onConfirm,
}: ConfirmDeleteDialogProps) {
  // Esc closes; Enter confirms (when not deleting).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      else if (e.key === 'Enter' && !deleting) onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, onConfirm, deleting]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Delete conversation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-xl bg-zinc-900 ring-1 ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-white/10 px-4 py-3 text-sm font-medium">
          Delete this conversation?
        </div>
        <div className="px-4 py-3 text-sm">
          <p>
            <span className="font-medium">{name}</span> will be deleted from
            the server. This cannot be undone.
          </p>
          {error && <p className="mt-2 text-xs text-rose-300">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-white/10 px-4 py-3">
          <Button variant="ghost" onPress={onCancel} isDisabled={deleting}>
            Cancel
          </Button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="rounded-md bg-rose-500/80 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-rose-500 disabled:cursor-wait disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface SessionRowProps {
  session: SessionMeta;
  isActive: boolean;
  isOpen: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

function SessionRow({
  session,
  isActive,
  isOpen,
  onSelect,
  onDelete,
}: SessionRowProps) {
  return (
    <li role="listitem">
      <div
        className={[
          'group relative flex items-center gap-2 px-3 py-1.5 text-sm transition-colors',
          isActive
            ? 'bg-white/8'
            : 'hover:bg-white/5 focus-within:bg-white/5',
        ].join(' ')}
      >
        <button
          type="button"
          onClick={onSelect}
          className="min-w-0 flex-1 text-start outline-none focus-visible:ring-1 focus-visible:ring-blue-400/60"
          title={session.name}
        >
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={[
                'inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full',
                isOpen ? 'bg-blue-400' : 'bg-transparent',
              ].join(' ')}
            />
            <span className="truncate">{session.name}</span>
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs opacity-60">
            <span>{relativeTime(session.updatedAt)}</span>
            {session.messageCount > 0 && (
              <span aria-label={`${session.messageCount} messages`}>
                · {session.messageCount} msg
              </span>
            )}
          </div>
        </button>
        <button
          type="button"
          aria-label={`Delete ${session.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="rounded p-1 text-xs opacity-0 transition group-hover:opacity-70 hover:bg-white/10 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60"
        >
          🗑
        </button>
      </div>
    </li>
  );
}

function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const seconds = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d ago`;
  return new Date(t).toLocaleDateString();
}
